import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Trash2, Plus, AlertTriangle, ListPlus, MapPin } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import Secao from "../WhatsAppModal/Secao";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import TagSelect from "../TagSelect";
import BarraDeSalvar from "../BarraDeSalvar";
import { MultiSelect } from "../ui/multi-select";
import CamposDoEndereco from "../CamposDoEndereco";
import {
	documentoValido,
	enderecoEmTexto,
	mascaraDoDocumento,
} from "../../helpers/entrega";

import { i18n } from "../../translate/i18n";

import PhoneInput from "../PhoneInput";
import usePaisFiscal from "../../hooks/usePaisFiscal";
import useModules from "../../hooks/useModules";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	number: Yup.string().min(8, "Muito curto").max(50, "Muito longo"),
	email: Yup.string().email("E-mail inválido"),
	document: Yup.string().test(
		"cpf-cnpj",
		"CPF tem 11 dígitos; CNPJ, 14.",
		(v) => documentoValido(v),
	),
});

// `pagina`: o mesmo formulário na aba Cadastro da página do contato
// (`/contacts/:id`), sem o Dialog e em cartões. Salvar não fecha nada: grava,
// relê e avisa `onSalvo`.
const ContactModal = ({
	open,
	onClose,
	contactId,
	initialValues,
	onSave,
	pagina = false,
	onSalvo,
}) => {
	// País padrão do seletor: numa operação portuguesa, abrir em Brasil faria
	// a equipe trocar o país em todo contato novo.
	const regras = usePaisFiscal();
	// No plano só de Status não há atendimento, equipe nem automação: dono,
	// empresa B2B, aniversário e "calar o robô" não teriam efeito nenhum.
	const { statusOnly } = useModules();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		birthDate: "",
		disableBot: false,
	};

	const [contact, setContact] = useState(initialState);
	// Etiquetas do CONTATO (as do atendimento são outras) e dono da conta.
	// As etiquetas não vão no corpo do contato: têm rota própria, porque o
	// vínculo é uma tabela à parte.
	const [tagIds, setTagIds] = useState([]);
	const [usuarios, setUsuarios] = useState([]);
	// Co-responsáveis vão por rota própria, como as etiquetas: o vínculo é uma
	// tabela à parte, não um campo do contato.
	const [coResponsaveis, setCoResponsaveis] = useState([]);
	const [organizacoes, setOrganizacoes] = useState([]);
	// Campos extras são exceção: quase ninguém usa, e em aberto eles roubam a
	// atenção de nome e número, que é o que importa.
	const [extrasAbertos, setExtrasAbertos] = useState(false);
	const [enderecoAberto, setEnderecoAberto] = useState(false);
	// O que veio do servidor, para a barra de salvar da página saber se
	// etiquetas e co-responsáveis (que não moram no Formik) mudaram.
	const [tagIdsSalvos, setTagIdsSalvos] = useState([]);
	const [donosSalvos, setDonosSalvos] = useState([]);
	const [recarga, setRecarga] = useState(0);
	const mesmaLista = (a, b) =>
		[...a].sort().join(",") === [...b].sort().join(",");
	const ligacoesMudaram =
		!mesmaLista(tagIds, tagIdsSalvos) || !mesmaLista(coResponsaveis, donosSalvos);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if ((!open && !pagina) || statusOnly) return;

		api
			.get("/users/")
			.then(({ data }) => setUsuarios(data.users || []))
			.catch(() => setUsuarios([]));

		api
			.get("/organizations")
			.then(({ data }) => setOrganizacoes(data.organizations || []))
			.catch(() => setOrganizacoes([]));
	}, [open, pagina, statusOnly]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					// `birthDate` volta nulo para quem não tem: num <input type="date">
					// isso vira campo não controlado e o React reclama no console.
					// Documento e endereço idem: nulos viram vazio, e o documento chega
					// só com dígitos — a tela mostra com pontos.
					setContact({
						...data,
						birthDate: data.birthDate || "",
						document: data.document ? mascaraDoDocumento(data.document) : "",
						address: data.address || {},
					});
					const etiquetas = (data.tags || []).map((tag) => tag.id);
					setTagIds(etiquetas);
					setTagIdsSalvos(etiquetas);
					api
						.get(`/contacts/${contactId}/owners`)
						.then(({ data: donos }) => {
							const ids = (donos || []).map((u) => u.id);
							setCoResponsaveis(ids);
							setDonosSalvos(ids);
						})
						.catch(() => setCoResponsaveis([]));
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues, recarga]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setTagIds([]);
		setCoResponsaveis([]);
	};

	const handleSaveContact = async values => {
		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, values);
				await api.put(`/contacts/${contactId}/tags`, { tagIds });
				await api.put(`/contacts/${contactId}/owners`, {
					userIds: coResponsaveis,
				});
				if (pagina) {
					setRecarga((n) => n + 1);
					onSalvo?.();
					toast.success(i18n.t("contactModal.success"));
					return;
				}
				handleClose();
			} else {
				const { data } = await api.post("/contacts", values);
				if (tagIds.length) {
					await api.put(`/contacts/${data.id}/tags`, { tagIds });
				}
				if (coResponsaveis.length) {
					await api.put(`/contacts/${data.id}/owners`, {
						userIds: coResponsaveis,
					});
				}
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	// CPF/CNPJ e endereço só existem na edição: o cadastro novo (POST) não
	// grava esses campos, e mostrá-los ali seria perder o que foi digitado.
	const campoDoDocumento = (touched, errors, setFieldValue) => (
		<div className="space-y-1.5">
			<Label htmlFor="document">CPF ou CNPJ</Label>
			<Field
				as={Input}
				id="document"
				name="document"
				inputMode="numeric"
				placeholder="000.000.000-00"
				onChange={(e) => setFieldValue("document", mascaraDoDocumento(e.target.value))}
			/>
			{touched.document && errors.document && (
				<p className="text-xs text-destructive">{errors.document}</p>
			)}
			<p className="text-xs leading-relaxed text-muted-foreground">
				Opcional. Vai para a nota fiscal dos pedidos desta pessoa.
			</p>
		</div>
	);

	const camposDoEndereco = (values, setFieldValue) => (
		<CamposDoEndereco
			idBase="contato-endereco"
			valor={values.address}
			onCampo={(campo, valor) => setFieldValue(`address.${campo}`, valor)}
		/>
	);

	const tituloDoCartao = (titulo, ajuda) => (
		<div className="space-y-0.5">
			<h2 className="text-sm font-semibold">{titulo}</h2>
			<p className="text-xs leading-relaxed text-muted-foreground">{ajuda}</p>
		</div>
	);

	const formulario = (
		<Formik
			initialValues={contact}
			enableReinitialize={true}
			validationSchema={ContactSchema}
			onSubmit={async (values, actions) => {
				await handleSaveContact(values);
				actions.setSubmitting(false);
			}}
		>
			{({ values, errors, touched, isSubmitting, setFieldValue, dirty, resetForm }) =>
				pagina ? (
					<Form className="space-y-4">
						<div className="grid items-start gap-4 lg:grid-cols-2 2xl:grid-cols-3">
							<section className="space-y-4 rounded-xl border bg-card p-4">
								{tituloDoCartao(
									"Identificação",
									"Como a equipe acha e fala com esta pessoa.",
								)}
								{/* Nome e telefone dividiam a linha e o número ficava cortado:
								    o seletor de país come metade da largura. Cada um na sua
								    linha, que é onde o número cabe inteiro. */}
								<div className="space-y-4">
									<div className="space-y-1.5">
										<Label htmlFor="name">{i18n.t("contactModal.form.name")}</Label>
										<Field
											as={Input}
											id="name"
											name="name"
											autoFocus={!pagina}
											placeholder="Ex.: Maria Silva"
										/>
										{touched.name && errors.name && (
											<p className="text-xs text-destructive">{errors.name}</p>
										)}
										<p className="text-xs leading-relaxed text-muted-foreground">
											{statusOnly
												? "É como a pessoa aparece na lista de contatos e na escolha de quem vê o status."
												: "É o que a equipe vê na lista de conversas."}
										</p>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="number">{i18n.t("contactModal.form.number")}</Label>
										<PhoneInput
											id="number"
											name="number"
											paisPadrao={regras.pais}
											value={values.number}
											onChange={(numero) => setFieldValue("number", numero)}
										/>
										{touched.number && errors.number && (
											<p className="text-xs text-destructive">{errors.number}</p>
										)}
										<p className="text-xs leading-relaxed text-muted-foreground">
											Escolha o país ao lado e digite só o número local. Colar o
											número completo, com +, também funciona.
										</p>
									</div>
								</div>
								{/* Contato sem número existe no banco e não serve para nada:
								    não dá para abrir conversa com ele. */}
								{!values.number && (
									<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
										<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
										{statusOnly
											? "Sem o número do WhatsApp o contato fica só na agenda e não entra em quem vê os status."
											: "Sem o número do WhatsApp o contato fica só na agenda: não dá para abrir conversa com ele nem incluí-lo em disparos."}
									</p>
								)}
								<div className="space-y-1.5">
									<Label htmlFor="email">{i18n.t("contactModal.form.email")}</Label>
									<Field
										as={Input}
										id="email"
										name="email"
										placeholder="maria@empresa.com.br"
									/>
									{touched.email && errors.email && (
										<p className="text-xs text-destructive">{errors.email}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										Opcional. Fica guardado como referência — o sistema não manda
										e-mail.
									</p>
								</div>
								{/* O parabéns automático existia no servidor e nesta tela não
								    havia onde cadastrar a data — o recurso ficava sem dado. */}
								{!statusOnly && (
								<div className="grid gap-4 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="birthDate">Aniversário</Label>
									<Field
										as={Input}
										id="birthDate"
										name="birthDate"
										type="date"
										className="w-auto"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Opcional. Com a data preenchida e o parabéns automático ligado
										em Configurações, esta pessoa recebe a mensagem no dia.
									</p>
								</div>
								{contactId && campoDoDocumento(touched, errors, setFieldValue)}
								</div>
								)}
								{/* Leitura antes de edição: o endereço salvo é uma linha; os
								    campos só abrem em "Alterar". */}
								{!statusOnly && contactId && (
									<div className="space-y-1.5">
										<div className="flex items-center justify-between gap-2">
											<p className="text-sm font-medium">Endereço de entrega</p>
											{(enderecoAberto || enderecoEmTexto(values.address)) && (
												<button
													type="button"
													onClick={() => setEnderecoAberto((v) => !v)}
													className="text-xs font-medium text-primary hover:underline"
												>
													{enderecoAberto ? "Pronto" : "Alterar"}
												</button>
											)}
										</div>
										{enderecoAberto ? (
											camposDoEndereco(values, setFieldValue)
										) : enderecoEmTexto(values.address) ? (
											<p className="text-sm wrap-break-word">{enderecoEmTexto(values.address)}</p>
										) : (
											<button
												type="button"
												onClick={() => setEnderecoAberto(true)}
												className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
											>
												<Plus className="h-3.5 w-3.5" />
												Adicionar endereço de entrega
											</button>
										)}
										<p className="text-xs leading-relaxed text-muted-foreground">
											O último endereço usado num pedido desta pessoa. A próxima venda já começa com ele.
										</p>
									</div>
								)}
							</section>

							{!statusOnly && (
								<section className="space-y-4 rounded-xl border bg-card p-4">
									{tituloDoCartao(
										"Relacionamento",
										"Quem cuida deste cliente, de qual empresa ele é e como o robô trata ele.",
									)}
									{!statusOnly && (
									<>
									<div className="space-y-1.5">
										<Label>Empresa</Label>
										<Select
											value={
												values.organizationId ? String(values.organizationId) : "nenhuma"
											}
											onValueChange={(v) =>
												setFieldValue(
													"organizationId",
													v === "nenhuma" ? null : Number(v),
												)
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="nenhuma">Sem empresa</SelectItem>
												{organizacoes.map((o) => (
													<SelectItem key={o.id} value={String(o.id)}>
														{o.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Para cliente que é empresa (B2B). Cadastre em Empresas, no
											menu ao lado de Contatos.
										</p>
									</div>

									<div className="space-y-1.5">
										<Label>Responsável</Label>
										<Select
											value={values.userId ? String(values.userId) : "nenhum"}
											onValueChange={(v) =>
												setFieldValue("userId", v === "nenhum" ? null : Number(v))
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="nenhum">Sem responsável</SelectItem>
												{usuarios.map((u) => (
													<SelectItem key={u.id} value={String(u.id)}>
														{u.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Quem cuida deste cliente. Com a distribuição automática
											ligada, o atendimento novo dele é oferecido ao dono primeiro
											— se ele estiver no departamento e disponível.
										</p>
									</div>

									<div className="space-y-1.5">
										<Label>Também acompanham</Label>
										<MultiSelect
											options={usuarios
												.filter((u) => u.id !== values.userId)
												.map((u) => ({ value: u.id, label: u.name }))}
											value={coResponsaveis}
											onChange={setCoResponsaveis}
											placeholder="Ninguém além do responsável"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Quem acompanha junto, sem ser o dono da conta. Não entra na
											distribuição de atendimento — para isso existe o responsável.
										</p>
									</div>

									{/* O backend já respeitava esta preferência (helpers/BotPause),
									    mas não havia como ligá-la: nem por tela, nem pela API. */}
									<div className="flex items-start justify-between gap-4 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="disableBot" className="text-sm">
												Não enviar mensagens automáticas
											</Label>
											<p className="text-xs leading-relaxed text-muted-foreground">
												Cala o robô para esta pessoa em qualquer atendimento: nada
												de chatbot, fluxo ou mensagem de ausência. A equipe
												continua conversando normalmente.
											</p>
										</div>
										<Switch
											id="disableBot"
											checked={Boolean(values.disableBot)}
											onCheckedChange={(marcado) =>
												setFieldValue("disableBot", marcado)
											}
										/>
									</div>
									</>
									)}
								</section>
							)}

							<section className="space-y-4 rounded-xl border bg-card p-4">
								{tituloDoCartao(
									"Etiquetas e anotações",
									"O que marca esta pessoa para sempre, e o que a equipe precisa saber dela.",
								)}
								<div className="space-y-1.5">
									<Label>Etiquetas do contato</Label>
									<TagSelect selectedTagIds={tagIds} onChange={setTagIds} />
									<p className="text-xs leading-relaxed text-muted-foreground">
										{statusOnly
											? "Agrupam as pessoas (clientes, revenda, atacado). Na hora de publicar, dá para escolher que só quem tem certa etiqueta veja o status."
											: "Marcam a PESSOA (revenda, atacado) e valem para sempre. As etiquetas do atendimento continuam sendo outras, na conversa."}
									</p>
								</div>
								<Secao
									titulo="Outras informações deste contato"
									resumo={
										values.extraInfo?.length
											? `${values.extraInfo.length} ${
													values.extraInfo.length === 1
														? "informação guardada"
														: "informações guardadas"
											  }`
											: "Nenhuma informação extra"
									}
									icone={ListPlus}
									aberta={pagina || extrasAbertos}
									onToggle={() => setExtrasAbertos((v) => !v)}
								>
									<p className="text-xs leading-relaxed text-muted-foreground">
										{statusOnly
											? "Anotações suas sobre a pessoa — documento, empresa, número do pedido. Ficam só aqui e não são enviadas para ninguém."
											: "Anotações suas sobre a pessoa — documento, empresa, número do pedido. Aparecem para a equipe ao abrir o contato e não são enviadas para ninguém."}
									</p>

									<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<div className="space-y-2">
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div key={`${index}-info`} className="flex items-center gap-2">
														<Field
															as={Input}
															placeholder="Ex.: Empresa"
															name={`extraInfo[${index}].name`}
														/>
														<Field
															as={Input}
															placeholder="Ex.: Padaria do Zé"
															name={`extraInfo[${index}].value`}
														/>
														<Button
															type="button"
															variant="ghost"
															size="icon"
															onClick={() => remove(index)}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													</div>
												))}
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() => push({ name: "", value: "" })}
											>
												<Plus className="h-3.5 w-3.5" />
												{i18n.t("contactModal.buttons.addExtraInfo")}
											</Button>
										</div>
									)}
									</FieldArray>
								</Secao>
							</section>
						</div>

						<BarraDeSalvar
							alterado={dirty || ligacoesMudaram}
							salvando={isSubmitting}
							onDescartar={() => {
								resetForm();
								setTagIds(tagIdsSalvos);
								setCoResponsaveis(donosSalvos);
							}}
						/>
					</Form>
				) : (
					<Form className="space-y-4">
						{/* Nome e telefone dividiam a linha e o número ficava cortado:
						    o seletor de país come metade da largura. Cada um na sua
						    linha, que é onde o número cabe inteiro. */}
						<div className="space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="name">{i18n.t("contactModal.form.name")}</Label>
								<Field
									as={Input}
									id="name"
									name="name"
									autoFocus={!pagina}
									placeholder="Ex.: Maria Silva"
								/>
								{touched.name && errors.name && (
									<p className="text-xs text-destructive">{errors.name}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									{statusOnly
										? "É como a pessoa aparece na lista de contatos e na escolha de quem vê o status."
										: "É o que a equipe vê na lista de conversas."}
								</p>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="number">{i18n.t("contactModal.form.number")}</Label>
								<PhoneInput
									id="number"
									name="number"
									paisPadrao={regras.pais}
									value={values.number}
									onChange={(numero) => setFieldValue("number", numero)}
								/>
								{touched.number && errors.number && (
									<p className="text-xs text-destructive">{errors.number}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Escolha o país ao lado e digite só o número local. Colar o
									número completo, com +, também funciona.
								</p>
							</div>
						</div>

						{/* Contato sem número existe no banco e não serve para nada:
						    não dá para abrir conversa com ele. */}
						{!values.number && (
							<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
								<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
								{statusOnly
									? "Sem o número do WhatsApp o contato fica só na agenda e não entra em quem vê os status."
									: "Sem o número do WhatsApp o contato fica só na agenda: não dá para abrir conversa com ele nem incluí-lo em disparos."}
							</p>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="email">{i18n.t("contactModal.form.email")}</Label>
							<Field
								as={Input}
								id="email"
								name="email"
								placeholder="maria@empresa.com.br"
							/>
							{touched.email && errors.email && (
								<p className="text-xs text-destructive">{errors.email}</p>
							)}
							<p className="text-xs leading-relaxed text-muted-foreground">
								Opcional. Fica guardado como referência — o sistema não manda
								e-mail.
							</p>
						</div>

						{/* O parabéns automático existia no servidor e nesta tela não
						    havia onde cadastrar a data — o recurso ficava sem dado. */}
						{!statusOnly && (
						<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="birthDate">Aniversário</Label>
							<Field
								as={Input}
								id="birthDate"
								name="birthDate"
								type="date"
								className="w-auto"
							/>
							<p className="text-xs leading-relaxed text-muted-foreground">
								Opcional. Com a data preenchida e o parabéns automático ligado
								em Configurações, esta pessoa recebe a mensagem no dia.
							</p>
						</div>
						{contactId && campoDoDocumento(touched, errors, setFieldValue)}
						</div>
						)}

						{!statusOnly && contactId && (
							<Secao
								titulo="Endereço de entrega"
								resumo={enderecoEmTexto(values.address) || "Nenhum endereço salvo"}
								icone={MapPin}
								aberta={enderecoAberto}
								onToggle={() => setEnderecoAberto((v) => !v)}
							>
								{camposDoEndereco(values, setFieldValue)}
							</Secao>
						)}

						<div className="space-y-1.5">
							<Label>Etiquetas do contato</Label>
							<TagSelect selectedTagIds={tagIds} onChange={setTagIds} />
							<p className="text-xs leading-relaxed text-muted-foreground">
								{statusOnly
									? "Agrupam as pessoas (clientes, revenda, atacado). Na hora de publicar, dá para escolher que só quem tem certa etiqueta veja o status."
									: "Marcam a PESSOA (revenda, atacado) e valem para sempre. As etiquetas do atendimento continuam sendo outras, na conversa."}
							</p>
						</div>

						{!statusOnly && (
						<>
						<div className="space-y-1.5">
							<Label>Empresa</Label>
							<Select
								value={
									values.organizationId ? String(values.organizationId) : "nenhuma"
								}
								onValueChange={(v) =>
									setFieldValue(
										"organizationId",
										v === "nenhuma" ? null : Number(v),
									)
								}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="nenhuma">Sem empresa</SelectItem>
									{organizacoes.map((o) => (
										<SelectItem key={o.id} value={String(o.id)}>
											{o.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="text-xs leading-relaxed text-muted-foreground">
								Para cliente que é empresa (B2B). Cadastre em Empresas, no
								menu ao lado de Contatos.
							</p>
						</div>

						<div className="space-y-1.5">
							<Label>Responsável</Label>
							<Select
								value={values.userId ? String(values.userId) : "nenhum"}
								onValueChange={(v) =>
									setFieldValue("userId", v === "nenhum" ? null : Number(v))
								}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="nenhum">Sem responsável</SelectItem>
									{usuarios.map((u) => (
										<SelectItem key={u.id} value={String(u.id)}>
											{u.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="text-xs leading-relaxed text-muted-foreground">
								Quem cuida deste cliente. Com a distribuição automática
								ligada, o atendimento novo dele é oferecido ao dono primeiro
								— se ele estiver no departamento e disponível.
							</p>
						</div>

						<div className="space-y-1.5">
							<Label>Também acompanham</Label>
							<MultiSelect
								options={usuarios
									.filter((u) => u.id !== values.userId)
									.map((u) => ({ value: u.id, label: u.name }))}
								value={coResponsaveis}
								onChange={setCoResponsaveis}
								placeholder="Ninguém além do responsável"
							/>
							<p className="text-xs leading-relaxed text-muted-foreground">
								Quem acompanha junto, sem ser o dono da conta. Não entra na
								distribuição de atendimento — para isso existe o responsável.
							</p>
						</div>

						{/* O backend já respeitava esta preferência (helpers/BotPause),
						    mas não havia como ligá-la: nem por tela, nem pela API. */}
						<div className="flex items-start justify-between gap-4 rounded-lg border p-3">
							<div className="space-y-0.5">
								<Label htmlFor="disableBot" className="text-sm">
									Não enviar mensagens automáticas
								</Label>
								<p className="text-xs leading-relaxed text-muted-foreground">
									Cala o robô para esta pessoa em qualquer atendimento: nada
									de chatbot, fluxo ou mensagem de ausência. A equipe
									continua conversando normalmente.
								</p>
							</div>
							<Switch
								id="disableBot"
								checked={Boolean(values.disableBot)}
								onCheckedChange={(marcado) =>
									setFieldValue("disableBot", marcado)
								}
							/>
						</div>
						</>
						)}

						<Secao
							titulo="Outras informações deste contato"
							resumo={
								values.extraInfo?.length
									? `${values.extraInfo.length} ${
											values.extraInfo.length === 1
												? "informação guardada"
												: "informações guardadas"
									  }`
									: "Nenhuma informação extra"
							}
							icone={ListPlus}
							aberta={pagina || extrasAbertos}
							onToggle={() => setExtrasAbertos((v) => !v)}
						>
							<p className="text-xs leading-relaxed text-muted-foreground">
								{statusOnly
									? "Anotações suas sobre a pessoa — documento, empresa, número do pedido. Ficam só aqui e não são enviadas para ninguém."
									: "Anotações suas sobre a pessoa — documento, empresa, número do pedido. Aparecem para a equipe ao abrir o contato e não são enviadas para ninguém."}
							</p>

							<FieldArray name="extraInfo">
							{({ push, remove }) => (
								<div className="space-y-2">
									{values.extraInfo &&
										values.extraInfo.length > 0 &&
										values.extraInfo.map((info, index) => (
											<div key={`${index}-info`} className="flex items-center gap-2">
												<Field
													as={Input}
													placeholder="Ex.: Empresa"
													name={`extraInfo[${index}].name`}
												/>
												<Field
													as={Input}
													placeholder="Ex.: Padaria do Zé"
													name={`extraInfo[${index}].value`}
												/>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													onClick={() => remove(index)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										))}
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="w-full"
										onClick={() => push({ name: "", value: "" })}
									>
										<Plus className="h-3.5 w-3.5" />
										{i18n.t("contactModal.buttons.addExtraInfo")}
									</Button>
								</div>
							)}
							</FieldArray>
						</Secao>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{i18n.t("contactModal.buttons.cancel")}
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
								{contactId ? "Salvar alterações" : "Criar contato"}
							</Button>
						</DialogFooter>
					</Form>
				)
			}
		</Formik>
	);

	if (pagina) return formulario;

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{contactId ? "Editar contato" : "Novo contato"}
					</DialogTitle>
					<DialogDescription>
						{contactId
							? "Muda só o cadastro na sua agenda. O contato não é avisado e nada é enviado para ele."
							: "Cadastrar aqui não avisa ninguém e não manda mensagem nenhuma. Só deixa a pessoa pronta na agenda, para você abrir a conversa quando quiser."}
					</DialogDescription>
				</DialogHeader>
				{formulario}
			</DialogContent>
		</Dialog>
	);
};

export default ContactModal;
